import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { fmtDate } from "@/constants/labels";
import { Plus, Save, Trash2, MessageCircle, FileText } from "lucide-react";

/**
 * Onglet admin pour l'assistant contextuel :
 *  - CRUD des entrées (question / mots-clés / réponse / actif / ordre)
 *  - Journal des questions posées (matched / non matched)
 *  - Demandes de contact ("Ce n'est pas ce que je cherchais")
 */
export default function AdminAssistant() {
  const [subtab, setSubtab] = useState("entries");
  const [entries, setEntries] = useState([]);
  const [logs, setLogs] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [editing, setEditing] = useState(null); // entry or {} for new
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [e, l, f] = await Promise.all([
        api.get("/admin/assistant/entries"),
        api.get("/admin/assistant/logs"),
        api.get("/admin/assistant/feedback"),
      ]);
      setEntries(e.data); setLogs(l.data); setFeedback(f.data);
    } catch (err) { toast.error(fmtError(err)); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({
    question: "", keywords: [], answer: "", active: true, order: (entries.length + 1) * 10,
  });
  const startEdit = (e) => setEditing({ ...e });
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const payload = {
      question: (editing.question || "").trim(),
      keywords: (editing.keywordsRaw !== undefined
        ? editing.keywordsRaw.split(",")
        : (editing.keywords || [])
      ).map(k => k.trim()).filter(Boolean),
      answer: (editing.answer || "").trim(),
      active: !!editing.active,
      order: Number(editing.order) || 0,
    };
    setBusy(true);
    try {
      if (editing.id) {
        await api.put(`/admin/assistant/entries/${editing.id}`, payload);
        toast.success("Entrée mise à jour");
      } else {
        await api.post("/admin/assistant/entries", payload);
        toast.success("Entrée créée");
      }
      setEditing(null);
      await load();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette entrée ?")) return;
    try {
      await api.delete(`/admin/assistant/entries/${id}`);
      toast.success("Entrée supprimée"); load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const SubTab = ({ id, label, count }) => (
    <button onClick={() => setSubtab(id)} data-testid={`assistant-subtab-${id}`}
      className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${
        subtab === id ? "bg-[#1B3A5C] text-white border-[#1B3A5C]" : "bg-white text-[#3E4C59] border-[#CBD5E1]"
      }`}>
      {label}{count !== undefined && <span className="ml-1 opacity-70">({count})</span>}
    </button>
  );

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap gap-2">
        <SubTab id="entries" label="Entrées" count={entries.length} />
        <SubTab id="logs" label="Journal" count={logs.length} />
        <SubTab id="feedback" label="Demandes de contact" count={feedback.length} />
      </div>

      {subtab === "entries" && (
        <>
          <div className="flex justify-end">
            <button onClick={startNew} className="btn-primary" data-testid="assistant-add-entry">
              <Plus size={14} /> Nouvelle entrée
            </button>
          </div>

          {editing && (
            <form onSubmit={save} className="card p-4 space-y-3" data-testid="assistant-entry-form">
              <div className="text-sm font-semibold text-[#1F2933]">
                {editing.id ? "Modifier l'entrée" : "Nouvelle entrée"}
              </div>
              <div>
                <label className="label">Question (telle qu'affichée en admin)</label>
                <input className="input" required maxLength={200}
                  value={editing.question}
                  onChange={(e) => setEditing({ ...editing, question: e.target.value })}
                  data-testid="assistant-entry-question" />
              </div>
              <div>
                <label className="label">
                  Mots-clés (séparés par une virgule — l'assistant match si l'un d'eux est présent)
                </label>
                <input className="input" placeholder="ex. profil, prestataire, metier, creer"
                  value={editing.keywordsRaw !== undefined
                    ? editing.keywordsRaw
                    : (editing.keywords || []).join(", ")}
                  onChange={(e) => setEditing({ ...editing, keywordsRaw: e.target.value })}
                  data-testid="assistant-entry-keywords" />
                <p className="text-[11px] text-[#3E4C59] mt-1">
                  Sans accent et en minuscules côté match (normalisation automatique). Utilisez plusieurs synonymes.
                </p>
              </div>
              <div>
                <label className="label">Réponse (affichée à l'utilisateur)</label>
                <textarea className="input min-h-[140px]" required maxLength={5000}
                  value={editing.answer}
                  onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
                  data-testid="assistant-entry-answer" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Ordre d'affichage</label>
                  <input type="number" className="input" value={editing.order}
                    onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                    data-testid="assistant-entry-order" />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!editing.active}
                      onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                      data-testid="assistant-entry-active" />
                    Active
                  </label>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={cancel} className="btn-outline">Annuler</button>
                <button type="submit" disabled={busy} className="btn-primary" data-testid="assistant-entry-save">
                  <Save size={14} /> {busy ? "…" : "Enregistrer"}
                </button>
              </div>
            </form>
          )}

          <div className="card overflow-x-auto">
            <table className="table-compact w-full">
              <thead>
                <tr>
                  <th>Ordre</th><th>Question</th><th>Mots-clés</th><th>Actif</th><th>Modifié</th><th></th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && <tr><td colSpan={6} className="text-center text-[#3E4C59]">Aucune entrée</td></tr>}
                {entries.map(e => (
                  <tr key={e.id} data-testid={`assistant-entry-row-${e.id}`}>
                    <td className="font-mono text-xs">{e.order}</td>
                    <td className="font-semibold">{e.question}</td>
                    <td className="text-xs text-[#3E4C59]">{(e.keywords || []).slice(0, 6).join(", ")}{(e.keywords || []).length > 6 ? "…" : ""}</td>
                    <td>{e.active
                      ? <span className="badge-valid">Actif</span>
                      : <span className="badge-muted">Désactivé</span>}</td>
                    <td className="text-xs">{fmtDate(e.updated_at)}</td>
                    <td className="flex gap-1">
                      <button onClick={() => startEdit(e)} className="btn-outline text-xs" data-testid={`assistant-entry-edit-${e.id}`}>Modifier</button>
                      <button onClick={() => remove(e.id)} className="btn-danger text-xs" data-testid={`assistant-entry-delete-${e.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {subtab === "logs" && (
        <div className="card overflow-x-auto" data-testid="assistant-logs-table">
          <div className="p-3 text-xs text-[#3E4C59] flex items-center gap-2 border-b border-[#E4E7EB]">
            <MessageCircle size={14} /> Toutes les questions posées à l'assistant. Les questions non matchées sont candidates à devenir de nouvelles entrées.
          </div>
          <table className="table-compact w-full">
            <thead>
              <tr>
                <th>Date</th><th>Utilisateur</th><th>Rôle</th><th>Question</th><th>Matché</th><th>Score</th><th>Feedback</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan={7} className="text-center text-[#3E4C59]">Aucune question posée pour l'instant</td></tr>}
              {logs.map(l => (
                <tr key={l.id} data-testid={`assistant-log-row-${l.id}`}>
                  <td className="text-xs whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  <td className="text-xs">{l.user_email}</td>
                  <td className="text-xs">{l.user_role}</td>
                  <td>{l.question}</td>
                  <td>{l.matched
                    ? <span className="badge-valid">Oui</span>
                    : <span className="badge-danger">Non</span>}</td>
                  <td className="text-xs">{l.matched_score}</td>
                  <td>{l.feedback_sent ? <span className="badge-warn">Contact envoyé</span> : <span className="text-[#3E4C59] text-xs">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {subtab === "feedback" && (
        <div className="card overflow-x-auto" data-testid="assistant-feedback-table">
          <div className="p-3 text-xs text-[#3E4C59] flex items-center gap-2 border-b border-[#E4E7EB]">
            <FileText size={14} /> Demandes de contact envoyées via « Ce n'est pas ce que je cherchais ». À traiter par e-mail depuis contact@voscompagnons.fr.
          </div>
          <table className="table-compact w-full">
            <thead>
              <tr>
                <th>Date</th><th>Utilisateur</th><th>Rôle</th><th>Question initiale</th><th>Message</th>
              </tr>
            </thead>
            <tbody>
              {feedback.length === 0 && <tr><td colSpan={5} className="text-center text-[#3E4C59]">Aucune demande de contact</td></tr>}
              {feedback.map(f => (
                <tr key={f.id} data-testid={`assistant-feedback-row-${f.id}`}>
                  <td className="text-xs whitespace-nowrap">{fmtDate(f.created_at)}</td>
                  <td className="text-xs">{f.user_email}</td>
                  <td className="text-xs">{f.user_role}</td>
                  <td className="text-xs italic max-w-xs">{f.question}</td>
                  <td className="text-sm whitespace-pre-wrap max-w-md">{f.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
